/**
 * The seven presets, with what each one sets and the path the audio takes.
 *
 * The diagrams are derived from the scenario data rather than listed by hand:
 * a chain that disagreed with the "what it sets" panel beside it would be worse
 * than no chain at all, and hand-listing a dozen of them is exactly how that
 * happens.
 */

import { useI18n } from '@/lib/i18n';
import { PageHead, Section } from '@/components/site/Page';
import '@/components/site/Page.scss';
import { FlowChain, type FlowHop, type FlowNode } from '@/components/site/FlowChain';
import {
  canServe,
  PROVIDER_KINDS,
  SCENARIOS,
  titleKey,
  type Scenario,
} from '@/components/site/scenarios';
import { LANGUAGE_NAME, counterpartLocale } from '@/components/site/demoScript';
import './Scenarios.scss';

interface Chain {
  nodes: FlowNode[];
  hops: FlowHop[];
}

export function Scenarios() {
  const { t, locale } = useI18n();
  const mine = LANGUAGE_NAME[locale];
  const theirs = LANGUAGE_NAME[counterpartLocale(locale)];

  /**
   * What the audio does, per leg.
   *
   * Outbound is you speaking: your language in, theirs out. Inbound is the
   * other side: theirs in, yours out. In a call, your spoken leg ends at the
   * virtual microphone and the application, and theirs, when Translation I
   * hear reads it to you, at your headphones; a leg that is not spoken ends on
   * a screen. Face-to-face has no call: both of you speak into the same
   * microphone, and each spoken leg ends in the listener's own earbud.
   */
  function chainsFor(scenario: Scenario): Chain[] {
    const out: Chain[] = [];

    if (scenario.beside) {
      const leg = (speaker: string, from: string, to: string, earbud: string, listener: string): Chain =>
        scenario.spoken
          ? {
              nodes: [
                { kind: 'person', label: t(speaker) },
                { kind: 'sokuji', label: t('flow.translates') },
                { kind: 'headphones', label: t(earbud) },
                { kind: 'person', label: t(listener) },
              ],
              hops: [
                { label: from, translated: false },
                { label: to, translated: true },
                { translated: true },
              ],
            }
          : {
              nodes: [
                { kind: 'person', label: t(speaker) },
                { kind: 'sokuji', label: t('flow.translates') },
                { kind: 'screen', label: t('flow.sameScreen') },
              ],
              hops: [
                { label: from, translated: false },
                { label: to, translated: true },
              ],
            };
      out.push(leg('flow.you', mine, theirs, 'flow.theirEarbud', 'flow.them'));
      out.push(leg('flow.them', theirs, mine, 'flow.yourEarbud', 'flow.you'));
      return out;
    }

    if (scenario.audio === 'speaker' || scenario.audio === 'both') {
      const nodes: FlowNode[] = [
        { kind: 'person', label: t('flow.you') },
        { kind: 'sokuji', label: t('flow.translates') },
      ];
      // Your own voice, untranslated, until Sokuji: neutral, as on the home page.
      const hops: FlowHop[] = [{ label: mine, translated: false }];

      if (scenario.spoken) {
        nodes.push(
          { kind: 'mic', label: t('flow.virtualMic') },
          { kind: 'app', label: t('flow.anyApp') },
          { kind: 'person', label: t('flow.them') },
        );
        hops.push({ label: theirs, translated: true }, { translated: true }, { translated: true });
      } else {
        // Only this one has an audience: the others put the text in front of
        // the person already holding the machine.
        const onScreen = scenario.id === 'subtitle-myself' ? 'flow.subtitles' : 'flow.yourScreen';
        nodes.push({ kind: 'screen', label: t(onScreen) });
        hops.push({ label: theirs, translated: true });
        if (scenario.id === 'subtitle-myself') {
          nodes.push({ kind: 'person', label: t('flow.audience') });
          hops.push({ translated: true });
        }
      }
      out.push({ nodes, hops });
    }

    if (scenario.audio === 'participant' || scenario.audio === 'both') {
      const nodes: FlowNode[] = [
        { kind: 'person', label: t('flow.them') },
        { kind: 'app', label: t('flow.anyAppSystem') },
        { kind: 'sokuji', label: t('flow.translates') },
      ];
      const hops: FlowHop[] = [
        { label: theirs, translated: false },
        { translated: false },
      ];
      // Only where the preset turns Translation I hear on. Where it leaves the
      // switch to you, the preset starts it off, and the chain shows the preset.
      if (scenario.heard === 'on') {
        nodes.push(
          { kind: 'headphones', label: t('flow.yourHeadphones') },
          { kind: 'person', label: t('flow.you') },
        );
        hops.push({ label: mine, translated: true }, { translated: true });
      } else {
        nodes.push({ kind: 'screen', label: t('flow.yourScreen') });
        hops.push({ label: mine, translated: true });
      }
      out.push({ nodes, hops });
    }

    return out;
  }

  const audioLabel = (s: Scenario) =>
    s.beside ? t('audio.besideMe') : s.audio === 'speaker' ? t('audio.speaker')
      : s.audio === 'participant' ? t('audio.participant') : t('audio.both');

  // Which sides are spoken, and to whom, is the answer — "yes" alone would
  // hide that two-way voice reads the other side to you as well, and that
  // understanding others leaves it to you.
  const spokenLabel = (s: Scenario) => {
    if (s.spoken && s.beside) return t('spoken.yesEarbuds');
    if (s.spoken && s.heard === 'on') return t('spoken.yesBoth');
    if (s.spoken) return t('spoken.yesVoice');
    if (s.heard === 'yours') return t('spoken.optional');
    return t('spoken.no');
  };

  return (
    <div className="site-page scenarios">
      <PageHead
        kicker={t('nav.scenarios')}
        title={t('scenarios.page.title')}
        lead={t('scenarios.page.lead')}
      />

      <Section title={t('scenarios.axes.title')} lead={t('scenarios.axes.lead')}>
        {SCENARIOS.map((scenario) => (
          <article className="scenarios__card" key={scenario.id}>
            <div className="scenarios__card-top">
              <div className="scenarios__card-text">
                {/* The preset's own name, as the app calls it. */}
                <div className="site-label scenarios__card-id">{scenario.id}</div>
                <h3>{t(titleKey(scenario.id))}</h3>
                <p>{t(`scenario.${scenario.id}.detail`)}</p>
                <p>
                  <strong>{t('scenarios.whoPicks')}</strong> {t(`scenario.${scenario.id}.who`)}
                </p>
              </div>

              {/* A definition list, not a table: these are name/value pairs, and
                  two columns of rows would claim a relationship between rows
                  that does not exist. */}
              <dl className="scenarios__sets">
                <div className="site-label scenarios__sets-head">{t('axis.sets')}</div>
                <div>
                  <dt className="site-label">{t('axis.whoseAudio')}</dt>
                  <dd>{audioLabel(scenario)}</dd>
                </div>
                <div>
                  <dt className="site-label">{t('axis.spokenAloud')}</dt>
                  <dd>{spokenLabel(scenario)}</dd>
                </div>
                <div>
                  <dt className="site-label">{t('axis.onScreen')}</dt>
                  <dd>{t(`scenario.${scenario.id}.onScreen`)}</dd>
                </div>
              </dl>
            </div>

            <div className="scenarios__chains">
              {chainsFor(scenario).map((chain, i) => (
                <FlowChain key={i} nodes={chain.nodes} hops={chain.hops} />
              ))}
            </div>
          </article>
        ))}
        <p className="scenarios__note">
          <strong>{t('scenarios.anyApp.lead')}</strong> {t('scenarios.anyApp.body')}
        </p>
      </Section>

      {/* Derived from the same `needs` field the app uses to close a scenario
          off, so the table cannot disagree with what the app will let you pick.
          A text-only provider cannot speak your side; one that always answers
          with audio cannot be held to subtitles. */}
      <Section title={t('scenarios.fit.title')} lead={t('scenarios.fit.lead')}>
        <p className="site-label scenarios__legend">
          <span className="scenarios__dot" /> {t('scenarios.fit.available')} &middot;{' '}
          <span className="scenarios__dot scenarios__dot--off" />{' '}
          {t('scenarios.fit.unavailable')}
        </p>

        <div className="scenarios__matrix" role="table">
          <div className="scenarios__row scenarios__row--head" role="row">
            <span />
            {PROVIDER_KINDS.map((k) => (
              <span key={k} className="site-label" role="columnheader">
                {t(`provider.${k}`)}
              </span>
            ))}
          </div>
          {SCENARIOS.map((s) => (
            <div className="scenarios__row" role="row" key={s.id}>
              <span className="scenarios__row-name" role="rowheader">
                {t(titleKey(s.id))}
              </span>
              {PROVIDER_KINDS.map((k) => (
                <span key={k} role="cell">
                  <span
                    className={`scenarios__dot ${canServe(s, k) ? '' : 'scenarios__dot--off'}`}
                    aria-label={t(canServe(s, k) ? 'scenarios.fit.available' : 'scenarios.fit.unavailable')}
                    role="img"
                  />
                </span>
              ))}
            </div>
          ))}
        </div>

        <p className="scenarios__note scenarios__note--tight">{t('scenarios.fit.note')}</p>
      </Section>
    </div>
  );
}
